import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * L'ÉTAT D'ERREUR UNIQUE (LOT-H10).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER EXISTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `404` et `/unauthorized` sont les deux seuls écrans sur 23 que la recette a
 * mesurés **hors de l'échelle typographique** : H1 à **48 px** là où le §2 en
 * prescrit 26 — « un H1 à 48 px est un bug, pas un choix de page » (DA-016).
 * Ils avaient été laissés de côté par H2, qui n'y avait pas posé son
 * `PageHeader` ; ces exclusions tombent ici.
 *
 * S'y ajoutait, sur `/unauthorized`, un fond de page peint à la main
 * (`min-h-screen bg-gradient-to-br from-slate-900…`) — l'anti-patron de la
 * « dalle noire » (DA-013). Une page ne peint pas son fond : le châssis le
 * fait.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * L'ARBITRAGE DE DA-016 : LE 404 RESTE UN EMBLÈME
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La recette laissait le choix ouvert : « soit les migrer sur `PageHeader`
 * (T1), soit — pour le “404” qui est un chiffre-emblème — ouvrir explicitement
 * un cran *display* dans le design system. À trancher, pas à subir. »
 *
 * **Tranché : les deux, sans nouveau cran global.**
 *
 *   · Le TITRE (« Page introuvable », « Accès refusé ») passe au cran **T1**,
 *     comme tout autre titre de page. C'est un `<h1>`.
 *   · Le CHIFFRE reste un emblème, dans la fente `emblem` — mais sa cote vit
 *     **ici, dans ce fichier**, et nulle part ailleurs.
 *
 * Pourquoi pas un septième cran dans `tailwind.config.js` ? Parce qu'un cran
 * déclaré est un cran **disponible partout** : H1 a fermé la porte des sept
 * traitements de titre, et publier un `text-display` la rouvrirait pour un
 * seul usage dans tout le produit. La règle de H1 vaut pour les crans, pas
 * pour les valeurs arbitraires confinées à une primitive — c'est exactement
 * l'argument que `StatCard` a retenu pour `min-[1100px]`.
 *
 * L'emblème est en `foreground-tertiary` : il se lit comme un **filigrane**,
 * pas comme un titre. C'est ce qui l'empêche de redevenir le H1 de 48 px que
 * DA-016 condamne — un titre à 64 px serait un bug plus gros que celui qu'on
 * corrige.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * TON : NEUTRE PAR DÉFAUT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Une page introuvable n'est pas une panne : c'est une adresse qui n'existe
 * pas. Le glyphe est donc neutre, comme celui de [`EmptyState`](EmptyState.tsx).
 * `tone="danger"` existe pour les rares cas où quelque chose a réellement
 * échoué — et il ne teinte que le glyphe, jamais le texte : c'est la règle du
 * §5sexies, posée par `Alert` (H9).
 */

/** Ton du glyphe. `neutral` par défaut : une 404 n'est pas une panne. */
export type ErrorStateTone = 'neutral' | 'danger'

const TONES: Record<ErrorStateTone, string> = {
  neutral: 'bg-background-secondary text-foreground-tertiary',
  danger: 'bg-danger-500/10 text-danger-400',
}

export interface ErrorStateProps {
  /**
   * L'EMBLÈME — le « 404 », et guère plus.
   *
   * Rendu au-dessus du titre, en très grand et en `foreground-tertiary`. La
   * fente est volontairement libre (`ReactNode`) et volontairement **rare** :
   * si un troisième écran demande un emblème, c'est le moment de se demander
   * ce qu'il raconte que son titre ne dit pas.
   */
  emblem?: ReactNode
  /** Glyphe Lucide. Ignoré quand `emblem` est fourni : un seul signal en tête. */
  icon?: LucideIcon
  tone?: ErrorStateTone
  /** Titre de la page, cran **T1**. Rendu en `<h1>` : il y en a un par écran (§2). */
  title: ReactNode
  /** Ce qui s'est passé, en une phrase. */
  description?: ReactNode
  /** Un fait de contexte — « Votre rôle : Agent ». Sous la description, discret. */
  detail?: ReactNode
  /** Les actions qui sortent de l'impasse. Deux au plus, annulation à gauche (§5). */
  actions?: ReactNode
  className?: string
}

export function ErrorState({
  emblem,
  icon: Icon,
  tone = 'neutral',
  title,
  description,
  detail,
  actions,
  className = '',
}: ErrorStateProps) {
  return (
    // Aucun `bg-*` ici : le fond appartient au châssis (DA-013). `min-h-[60vh]`
    // et non `min-h-screen` — la page vit DANS le `<main>` de l'`AppShell`, sous
    // un en-tête qui occupe déjà sa hauteur ; `100vh` ferait défiler l'écran
    // d'exactement la hauteur du bandeau.
    <div className={`flex min-h-[60vh] flex-col items-center justify-center px-6 text-center ${className}`.trim()}>
      {emblem ? (
        /* La cote de l'emblème vit ICI et nulle part ailleurs — pas de cran
           `display` dans `tailwind.config.js` (voir l'en-tête du fichier).
           `leading-none` : sans lui, un chiffre de 64 px traîne ~20 px
           d'interligne et le titre décroche du bloc. `tabular-nums` : « 404 »
           et « 403 » doivent avoir exactement la même largeur. */
        <p className="mb-2 text-[64px] font-bold leading-none tracking-tight text-foreground-tertiary tabular-nums">
          {emblem}
        </p>
      ) : (
        Icon && (
          <span
            aria-hidden="true"
            className={`mb-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-control ${TONES[tone]}`}
          >
            <Icon className="h-6 w-6" />
          </span>
        )
      )}

      <h1 className="text-t1 text-foreground">{title}</h1>

      {description && (
        <p className="mt-2 max-w-[46ch] text-t4 text-foreground-secondary">{description}</p>
      )}

      {detail && <p className="mt-2 text-t5 text-foreground-tertiary">{detail}</p>}

      {actions && <div className="mt-6 flex flex-wrap items-center justify-center gap-3">{actions}</div>}
    </div>
  )
}

export default ErrorState
